import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useAnalyses, useStartAnalysis, useAnalysisStatus, useDeleteAnalysis,
  useUploadAnalysisAsset, useDeleteAnalysisAsset, useDispatchAnalysis, useAnalysis,
  useSplitAnalysisToCluster,
} from '../hooks/useAnalysis';
import { useCreateCluster } from '../hooks/useAnalysisClusters';
import { useContacts } from '../hooks/useContacts';
import { useLlmProviders } from '../hooks/useLlm';
import { useTags } from '../hooks/useTags';
import useProjectStore from '../stores/projectStore';
import Badge from '../components/ui/Badge';
import ScorePill from '../components/ui/ScorePill';
import Modal from '../components/ui/Modal';
import ProjectContextBadge from '../components/layout/ProjectContextBadge';

const DISCIPLINES = [
  { key: '', label: 'Alle' },
  { key: 'cro', label: 'CRO', color: '#0C9488' },
  { key: 'sea', label: 'SEA', color: '#3B82F6' },
  { key: 'seo', label: 'SEO', color: '#8B5CF6' },
  { key: 'ux', label: 'UX', color: '#F59E0B' },
];

const DISCIPLINE_BADGE = {
  cro: { bg: '#E6F4F2', color: '#0C9488' },
  sea: { bg: '#DBEAFE', color: '#3B82F6' },
  seo: { bg: '#EDE9FE', color: '#8B5CF6' },
  ux: { bg: '#FEF3C7', color: '#F59E0B' },
};

function guessPageTypeSlug(url, tags) {
  if (!url || !tags?.length) return tags?.[0]?.slug || '';
  let path = '';
  try {
    path = new URL(url.startsWith('http') ? url : `https://${url}`).pathname.toLowerCase();
  } catch {
    path = url.toLowerCase();
  }
  const rules = [
    { re: /^\/?$/, keys: ['home', 'start', 'landing'] },
    { re: /(produkt|product|shop|artikel\/|item)/, keys: ['product', 'produkt', 'pdp'] },
    { re: /(kategorie|category|sortiment)/, keys: ['category', 'kategorie', 'plp'] },
    { re: /(blog|magazin|news|ratgeber)/, keys: ['blog', 'article', 'artikel'] },
    { re: /(kontakt|contact)/, keys: ['contact', 'kontakt'] },
    { re: /(ueber-uns|about|team)/, keys: ['about', 'ueber'] },
    { re: /(preise|pricing|tarif)/, keys: ['pricing', 'preise'] },
    { re: /(checkout|warenkorb|cart)/, keys: ['checkout', 'cart'] },
  ];
  for (const rule of rules) {
    if (rule.re.test(path)) {
      const match = tags.find((t) => rule.keys.some((k) => t.slug?.toLowerCase().includes(k) || t.name?.toLowerCase().includes(k)));
      if (match) return match.slug;
    }
  }
  return tags[0]?.slug || '';
}

export default function Analyses() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [discipline, setDiscipline] = useState('');
  const { data: analysesData, isLoading } = useAnalyses(discipline ? { discipline } : {});
  const { data: contactsData } = useContacts();
  const { data: llmData } = useLlmProviders();
  const { data: tagsData } = useTags();
  const startMutation = useStartAnalysis();
  const deleteMutation = useDeleteAnalysis();
  const dispatchMutation = useDispatchAnalysis();
  const createCluster = useCreateCluster();
  const currentProject = useProjectStore((s) => s.projects.find((p) => p.id === s.currentProjectId));

  const analyses = analysesData?.data || [];
  const contacts = contactsData?.data || [];
  const providers = llmData?.providers || [];
  const allTags = tagsData || [];

  const projectDomainUrl = currentProject?.domain
    ? (currentProject.domain.startsWith('http') ? currentProject.domain : `https://${currentProject.domain}`)
    : '';

  const [showNew, setShowNew] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [form, setForm] = useState({
    url: '', page_type: '', contact_id: '', provider: '',
    source_mode: 'live', discipline: 'cro',
  });
  const [analysisId, setAnalysisId] = useState(null);
  const { data: status } = useAnalysisStatus(analysisId);

  // Asset-Upload Modal nach Erstellung im assets-Mode
  const [assetUploadAnalysisId, setAssetUploadAnalysisId] = useState(null);

  // Cluster-Auswahl
  const [selected, setSelected] = useState([]);
  const [showClusterModal, setShowClusterModal] = useState(false);
  const [clusterForm, setClusterForm] = useState({ title: '', synthesis_prompt_extra: '' });

  const toggleSelected = (id) => setSelected((s) => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const openNewAnalysis = (prefilledUrl) => {
    const url = prefilledUrl || projectDomainUrl || '';
    setForm({
      url,
      page_type: guessPageTypeSlug(url, allTags),
      contact_id: '',
      provider: '',
      source_mode: 'live',
      discipline: 'cro',
    });
    setShowAdvanced(false);
    setShowNew(true);
  };

  useEffect(() => {
    if (searchParams.get('new') === '1' && allTags.length > 0 && !showNew) {
      openNewAnalysis();
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams, allTags.length]);

  const handleStart = async () => {
    if (!form.url || !form.page_type) return;
    try {
      const result = await startMutation.mutateAsync({
        url: form.url,
        page_type: form.page_type,
        contact_id: form.contact_id || undefined,
        provider: form.provider || undefined,
        source_mode: form.source_mode,
        discipline: form.discipline,
      });
      setShowNew(false);
      if (form.source_mode === 'assets') {
        setAssetUploadAnalysisId(result.id);
      } else {
        setAnalysisId(result.id);
      }
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCreateCluster = async () => {
    if (!clusterForm.title || selected.length < 1) return;
    try {
      const c = await createCluster.mutateAsync({
        project_id: currentProject?.id,
        title: clusterForm.title,
        analysis_ids: selected,
        synthesis_prompt_extra: clusterForm.synthesis_prompt_extra || undefined,
        auto_synthesize: true,
      });
      setShowClusterModal(false);
      setSelected([]);
      navigate(`/cro/clusters/${c.id}`);
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.message || err.message));
    }
  };

  if (analysisId && status && status.status !== 'done' && status.status !== 'failed') {
    const prog = status.status === 'processing' ? 65 : 15;
    return (
      <div className="max-w-[560px] mx-auto mt-12">
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-10 text-center">
          <div className="w-[52px] h-[52px] rounded-full bg-teal-light text-teal flex items-center justify-center text-xl mx-auto mb-3.5 animate-pulse">⟳</div>
          <div className="text-[14.5px] font-semibold mb-1">Analyse läuft…</div>
          <div className="text-[13px] text-gray-500 mb-5">
            {status.status === 'processing' ? 'Faktoren werden analysiert' : 'Analyse wird vorbereitet'}
          </div>
          <div className="bg-bg rounded-full h-[7px] overflow-hidden max-w-[360px] mx-auto mb-2.5">
            <div className="bg-teal h-full rounded-full transition-all duration-1000" style={{ width: `${prog}%` }} />
          </div>
          <div className="text-[19px] font-bold text-teal">{prog}%</div>
          <button onClick={() => setAnalysisId(null)} className="mt-5 text-[13px] text-gray-500 hover:text-gray-700">
            Zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-5">
        <div>
          <ProjectContextBadge />
          <h1 className="text-[23px] font-bold mb-0.5">Analysen</h1>
          <p className="text-gray-500 text-[13.5px]">CRO/SEA/SEO/UX-Analysen — einzeln oder als Cluster bündeln</p>
        </div>
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <button
              onClick={() => { setClusterForm({ title: `Cluster · ${new Date().toLocaleDateString('de')}`, synthesis_prompt_extra: '' }); setShowClusterModal(true); }}
              className="px-4 py-2.5 bg-sidebar text-white rounded-lg text-[13px] font-medium hover:opacity-90"
            >
              ◇ Cluster aus {selected.length} bilden
            </button>
          )}
          <button
            onClick={() => navigate('/cro/clusters')}
            className="px-3 py-2.5 border border-gray-200 rounded-lg text-[13px] hover:bg-gray-50"
          >
            Cluster
          </button>
          <button
            onClick={() => openNewAnalysis()}
            className="px-4 py-2.5 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90"
          >
            + Neue Analyse
          </button>
        </div>
      </div>

      {/* Discipline-Filter */}
      <div className="flex gap-1 mb-4 bg-bg rounded-lg p-1 w-fit">
        {DISCIPLINES.map((d) => (
          <button
            key={d.key || 'all'}
            onClick={() => setDiscipline(d.key)}
            className={`px-3 py-1.5 rounded-md text-[12.5px] transition-colors ${discipline === d.key ? 'bg-white shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'}`}
            style={discipline === d.key && d.color ? { color: d.color } : {}}
          >
            {d.label}
          </button>
        ))}
      </div>

      {analysisId && status?.status === 'done' && (
        <div className="bg-green-light border border-green/20 rounded-xl p-4 mb-5 flex items-center justify-between">
          <div>
            <span className="text-green font-semibold text-[14px]">Analyse abgeschlossen</span>
            <span className="text-[13px] text-gray-600 ml-3">Score: {status.score} · Provider: {status.provider}</span>
          </div>
          <button onClick={() => setAnalysisId(null)} className="text-[13px] text-green font-medium hover:underline">OK</button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 text-left">
              <th className="px-3 py-3.5 w-8"></th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">URL</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Disziplin</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Kunde</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Typ</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Datum</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Score</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Status</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {analyses.map((a) => {
              const dBadge = DISCIPLINE_BADGE[a.discipline || 'cro'] || DISCIPLINE_BADGE.cro;
              return (
                <tr
                  key={a.id}
                  className={`border-b border-gray-100 last:border-0 hover:bg-gray-50/50 transition-colors ${selected.includes(a.id) ? 'bg-teal-light/30' : ''}`}
                >
                  <td className="px-3 py-3.5" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="cursor-pointer"
                      checked={selected.includes(a.id)}
                      onChange={() => toggleSelected(a.id)}
                      disabled={a.status !== 'done'}
                      title={a.status !== 'done' ? 'Nur abgeschlossene Analysen' : ''}
                    />
                  </td>
                  <td className="px-5 py-3.5 text-[13px] font-medium truncate max-w-[260px]">
                    <span className={a.status === 'done' ? 'cursor-pointer hover:underline' : ''} onClick={() => a.status === 'done' && navigate(`/cro/analyses/${a.id}`)}>
                      {a.source_mode === 'assets' && <span className="text-gray-400 mr-1">⤴</span>}
                      {a.url}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <Badge bg={dBadge.bg} color={dBadge.color}>{(a.discipline || 'cro').toUpperCase()}</Badge>
                  </td>
                  <td className="px-5 py-3.5 text-[12.5px]">
                    {a.contact ? (
                      <button onClick={() => navigate(`/contacts/${a.contact.id}`)} className="text-teal hover:underline">{a.contact.name}</button>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-5 py-3.5"><Badge bg="#F3F4F6" color="#6B7280">{a.page_type}</Badge></td>
                  <td className="px-5 py-3.5 text-[12.5px] text-gray-500">{new Date(a.created_at).toLocaleDateString('de')}</td>
                  <td className="px-5 py-3.5">{a.score ? <ScorePill score={a.score} /> : '—'}</td>
                  <td className="px-5 py-3.5">
                    <Badge
                      bg={a.status === 'done' ? '#D1FAE5' : a.status === 'processing' ? '#FEF3C7' : a.status === 'failed' ? '#FEE2E2' : '#F3F4F6'}
                      color={a.status === 'done' ? '#10B981' : a.status === 'processing' ? '#F59E0B' : a.status === 'failed' ? '#EF4444' : '#6B7280'}
                    >
                      {a.status}
                    </Badge>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    {a.status === 'pending' && a.source_mode === 'assets' && (
                      <button onClick={() => setAssetUploadAnalysisId(a.id)} className="text-[12px] text-teal hover:underline mr-3 font-medium">
                        Assets
                      </button>
                    )}
                    <button
                      onClick={() => { if (confirm('Analyse wirklich löschen?')) deleteMutation.mutate(a.id); }}
                      className="text-[12px] text-red-500 hover:text-red-700 font-medium"
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {analyses.length === 0 && !isLoading && (
          <div className="py-12 text-center text-gray-400 text-sm">Noch keine Analysen vorhanden.</div>
        )}
      </div>

      {/* Neue Analyse Modal */}
      {showNew && (
        <Modal onClose={() => setShowNew(false)}>
          <div className="w-[540px]">
            <h2 className="text-[17px] font-bold mb-1">Neue Analyse</h2>
            <p className="text-[13px] text-gray-500 mb-4">
              {currentProject?.name ? `Seite/Kanal von ${currentProject.name} analysieren` : 'URL/Kanal analysieren'}
            </p>

            {/* Disziplin */}
            <div className="mb-3">
              <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Disziplin</label>
              <div className="flex gap-2">
                {DISCIPLINES.filter(d => d.key).map((d) => (
                  <button
                    key={d.key}
                    onClick={() => setForm({ ...form, discipline: d.key })}
                    className={`flex-1 rounded-lg py-1.5 px-2 text-[12px] border transition-all ${form.discipline === d.key ? 'text-white font-semibold' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}
                    style={form.discipline === d.key ? { backgroundColor: d.color, borderColor: d.color } : {}}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Source-Mode */}
            <div className="mb-3">
              <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Quelle</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setForm({ ...form, source_mode: 'live' })}
                  className={`flex-1 rounded-lg py-1.5 px-2 text-[12px] border ${form.source_mode === 'live' ? 'bg-sidebar border-sidebar text-white font-semibold' : 'bg-white border-gray-200 text-gray-500'}`}
                >
                  Live-URL (öffentlich)
                </button>
                <button
                  onClick={() => setForm({ ...form, source_mode: 'assets' })}
                  className={`flex-1 rounded-lg py-1.5 px-2 text-[12px] border ${form.source_mode === 'assets' ? 'bg-sidebar border-sidebar text-white font-semibold' : 'bg-white border-gray-200 text-gray-500'}`}
                >
                  Screenshots/Notizen (Login-Bereich)
                </button>
              </div>
              {form.source_mode === 'assets' && (
                <p className="text-[11px] text-gray-400 mt-1.5">
                  Nach dem Anlegen kannst du Screenshots hochladen (z.B. Checkout, Mobile-Flow). Die KI analysiert sie multimodal.
                </p>
              )}
            </div>

            <div className="mb-3">
              <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">
                {form.source_mode === 'assets' ? 'Bezeichnung / interne URL' : 'URL'}
              </label>
              <input
                autoFocus
                className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal"
                placeholder={form.source_mode === 'assets' ? 'shop.example.com/checkout (intern)' : 'https://website.de/seite'}
                value={form.url}
                onChange={(e) => {
                  const url = e.target.value;
                  setForm((f) => ({ ...f, url, page_type: f.page_type || guessPageTypeSlug(url, allTags) }));
                }}
              />
            </div>

            <div className="mb-3">
              <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Seitentyp</label>
              <div className="flex flex-wrap gap-2">
                {allTags.map((t) => (
                  <button
                    key={t.slug}
                    onClick={() => setForm({ ...form, page_type: t.slug })}
                    className={`rounded-lg py-1.5 px-2.5 text-[11px] border transition-all ${form.page_type === t.slug ? 'text-white font-semibold' : 'bg-bg border-gray-200 text-gray-500 hover:border-gray-300'}`}
                    style={form.page_type === t.slug ? { backgroundColor: t.color, borderColor: t.color } : {}}
                  >
                    {t.name} ({t.factors_count})
                  </button>
                ))}
              </div>
            </div>

            <button onClick={() => setShowAdvanced((v) => !v)} className="text-[11.5px] text-gray-500 hover:text-gray-700 mb-2">
              {showAdvanced ? '▾' : '▸'} Erweitert (Kunde, LLM Provider)
            </button>

            {showAdvanced && (
              <div className="bg-gray-50 rounded-lg px-3 py-3 mb-4 space-y-3">
                <div>
                  <label className="text-[11px] text-gray-500 block mb-1 font-medium">Kunde</label>
                  <select
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[12.5px] outline-none focus:border-teal bg-white"
                    value={form.contact_id}
                    onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
                  >
                    <option value="">Kein Kunde zugeordnet</option>
                    {contacts.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-gray-500 block mb-1.5 font-medium">LLM Provider</label>
                  <div className="flex gap-2">
                    {[{ key: '', label: 'Standard' }, ...providers.map(p => ({ key: p.name, label: p.name === 'openai' ? 'OpenAI' : p.name === 'claude' ? 'Claude' : 'Self-Hosted', available: p.available }))].map((p) => (
                      <button
                        key={p.key}
                        onClick={() => setForm({ ...form, provider: p.key })}
                        disabled={p.available === false}
                        className={`flex-1 rounded-lg py-1.5 px-2 text-[11.5px] border transition-all ${form.provider === p.key ? 'bg-sidebar border-sidebar text-white font-semibold' : p.available === false ? 'bg-gray-100 border-gray-200 text-gray-300 cursor-not-allowed' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2.5 justify-end">
              <button onClick={() => setShowNew(false)} className="border border-gray-200 rounded-lg px-4 py-2 text-[13px] hover:bg-gray-50">Abbrechen</button>
              <button
                onClick={handleStart}
                disabled={!form.url || !form.page_type || startMutation.isPending}
                className="bg-teal text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
              >
                {startMutation.isPending ? 'Wird angelegt…' : (form.source_mode === 'assets' ? 'Analyse anlegen + Assets hochladen →' : 'Analyse starten →')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Asset-Upload Modal */}
      {assetUploadAnalysisId && (
        <AssetUploadModal
          analysisId={assetUploadAnalysisId}
          onClose={() => setAssetUploadAnalysisId(null)}
          onDispatch={() => {
            dispatchMutation.mutate(assetUploadAnalysisId);
            setAnalysisId(assetUploadAnalysisId);
            setAssetUploadAnalysisId(null);
          }}
        />
      )}

      {/* Cluster-Modal */}
      {showClusterModal && (
        <Modal onClose={() => setShowClusterModal(false)}>
          <div className="w-[480px]">
            <h2 className="text-[17px] font-bold mb-1">Cluster bilden</h2>
            <p className="text-[13px] text-gray-500 mb-4">
              {selected.length} Analysen werden zu einer projektweiten Synthese gebündelt (Muster, Widersprüche, Roadmap).
            </p>
            <div className="mb-3">
              <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Titel</label>
              <input
                autoFocus
                className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal"
                value={clusterForm.title}
                onChange={(e) => setClusterForm({ ...clusterForm, title: e.target.value })}
              />
            </div>
            <div className="mb-4">
              <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Zusatz-Anweisung an die KI (optional)</label>
              <textarea
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal"
                placeholder="z.B. 'Fokussiere auf Trust-Signale' oder 'Vergleiche SEA-LP mit organischer Landingpage'"
                value={clusterForm.synthesis_prompt_extra}
                onChange={(e) => setClusterForm({ ...clusterForm, synthesis_prompt_extra: e.target.value })}
              />
            </div>
            <div className="flex gap-2.5 justify-end">
              <button onClick={() => setShowClusterModal(false)} className="border border-gray-200 rounded-lg px-4 py-2 text-[13px] hover:bg-gray-50">Abbrechen</button>
              <button
                onClick={handleCreateCluster}
                disabled={!clusterForm.title || createCluster.isPending}
                className="bg-sidebar text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
              >
                {createCluster.isPending ? 'Wird erstellt…' : 'Cluster erstellen + synthetisieren →'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function AssetUploadModal({ analysisId, onClose, onDispatch }) {
  const navigate = useNavigate();
  const { data: analysis } = useAnalysis(analysisId);
  const upload = useUploadAnalysisAsset();
  const del = useDeleteAnalysisAsset();
  const split = useSplitAnalysisToCluster();
  const [caption, setCaption] = useState('');
  const [note, setNote] = useState('');

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await upload.mutateAsync({ analysisId, file, caption, type: 'screenshot' });
    setCaption('');
    e.target.value = '';
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await upload.mutateAsync({ analysisId, type: 'note', note });
    setNote('');
  };

  const assets = analysis?.assets || [];

  return (
    <Modal onClose={onClose}>
      <div className="w-[640px]">
        <h2 className="text-[17px] font-bold mb-1">Assets hochladen</h2>
        <p className="text-[13px] text-gray-500 mb-4">
          {analysis?.url} · Lade Screenshots hoch und/oder ergänze Notizen.
        </p>

        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Screenshot hochladen</label>
          <input
            type="text"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[12.5px] outline-none focus:border-teal mb-2"
            placeholder="Bildunterschrift (optional, z.B. 'Checkout Schritt 2 — Versand')"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
          />
          <input
            type="file"
            accept="image/*"
            onChange={onFile}
            className="text-[12px]"
          />
          {upload.isPending && <div className="text-[11px] text-gray-500 mt-1">Wird hochgeladen…</div>}
        </div>

        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Notiz</label>
          <textarea
            rows={3}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[12.5px] outline-none focus:border-teal mb-2"
            placeholder="Beschreibung, Kontext, Beobachtungen…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <button onClick={addNote} disabled={!note.trim()} className="text-[12px] text-teal hover:underline disabled:text-gray-300">
            + Notiz hinzufügen
          </button>
        </div>

        {assets.length > 0 && (
          <div className="mb-4">
            <div className="text-[12px] text-gray-500 mb-2 font-medium">{assets.length} Asset(s) angehängt</div>
            <ul className="space-y-1.5">
              {assets.map((a) => (
                <li key={a.id} className="flex items-start justify-between bg-white border border-gray-100 rounded-lg px-3 py-2 text-[12px]">
                  <div className="flex-1 min-w-0 pr-3">
                    <div className="font-medium text-gray-700">{a.type === 'note' ? 'Notiz' : 'Screenshot'}{a.caption ? ` · ${a.caption}` : ''}</div>
                    {a.note && <div className="text-gray-500 truncate">{a.note}</div>}
                  </div>
                  <button onClick={() => del.mutate({ analysisId, assetId: a.id })} className="text-red-400 hover:text-red-600 text-[11px]">×</button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex gap-2.5 justify-end items-center">
          {assets.filter(a => a.type === 'screenshot').length >= 2 && (
            <button
              onClick={async () => {
                const title = prompt('Titel für den Funnel-Cluster:', `Funnel · ${analysis?.url || ''}`);
                if (!title) return;
                const res = await split.mutateAsync({ id: analysisId, title, auto_synthesize: false, delete_original: true });
                onClose();
                navigate(`/cro/clusters/${res.cluster.id}`);
              }}
              disabled={split.isPending}
              className="bg-sidebar text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
              title="Aus jedem Screenshot eine eigene Analyse + automatischer Cluster (Funnel-Synthese)"
            >
              {split.isPending ? 'Wird gesplittet…' : '◇ Als Funnel-Cluster splitten'}
            </button>
          )}
          <button onClick={onClose} className="border border-gray-200 rounded-lg px-4 py-2 text-[13px] hover:bg-gray-50">Später</button>
          <button
            onClick={onDispatch}
            disabled={assets.length === 0}
            className="bg-teal text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
          >
            Als 1 Analyse starten →
          </button>
        </div>
      </div>
    </Modal>
  );
}
